/** The Store — an editable cart the learner builds, then confirms.
 *
 * Content is sold per level as a Course, Practice, or the Complete bundle
 * (both, cheaper). The learner picks one option per level plus any credit
 * packs; the running total updates live; Confirm unlocks everything at once.
 *
 * A level already (partly) owned only offers what's still missing — own the
 * course, and only Practice is on the table. Fully-owned levels read as
 * "Owned".
 *
 * The recommendation card links here with ?rec=pack.<lvl>.<kind>, which
 * pre-selects that option so the suggested plan is one click from done.
 *
 * CONFIRM-TO-GRANT: no card is charged yet (à-la-carte payment isn't wired
 * and prices are placeholders) — Confirm grants the packs directly. When
 * payment lands, only this button's handler changes.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import * as api from "../../api/store";
import type { OrderInstructions, Store } from "../../api/store";
import { formatMinor } from "../billing/BillingPage";
import { Badge, Button, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

const LEVEL_ORDER = ["A1", "A2", "B1", "B2", "C1"];
const KINDS = ["learn", "practice", "complete"] as const;
type Kind = (typeof KINDS)[number];

const KIND_LABEL: Record<Kind, MessageKey> = {
  learn: "store.kind.learn",
  practice: "store.kind.practice",
  complete: "store.kind.complete",
};

function packCode(level: string, kind: Kind): string {
  return `pack.${level.toLowerCase()}.${kind}`;
}

/** Which options a level still offers, given what's already owned. */
function offered(store: Store, level: string): Kind[] {
  const lv = level.toUpperCase();
  const ownsLearn = store.entitlements.learn.some((l) => l.toUpperCase() === lv);
  const ownsPractice = store.entitlements.practice.some((l) => l.toUpperCase() === lv);
  if (ownsLearn && ownsPractice) return [];
  if (ownsLearn) return ["practice"];
  if (ownsPractice) return ["learn"];
  return ["learn", "practice", "complete"];
}

export function StorePage() {
  const t = useT();
  const { user } = useAuth();
  // Only the account owner buys. In a school the admin orders for everyone;
  // a pupil who lands here by URL gets told who to ask, not a dead button.
  const canOrder = user?.role === "tenant_admin";
  const [params] = useSearchParams();
  const [store, setStore] = useState<Store | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [levelSel, setLevelSel] = useState<Record<string, Kind>>({});
  const [creditSel, setCreditSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState<api.OrderInstructions | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    api.getStore().then(setStore).catch(() => setStore(null));
    api.myCredits().then((c) => setBalance(c.balance)).catch(() => setBalance(null));
  };
  useEffect(load, []);

  // Pre-select the recommended option once the catalogue is in.
  const rec = params.get("rec");
  useEffect(() => {
    if (!store || !rec) return;
    const m = /^pack\.([a-z0-9]+)\.(learn|practice|complete)$/.exec(rec);
    if (!m) return;
    const level = m[1].toUpperCase();
    const kind = m[2] as Kind;
    // Only offer what's still buyable; a manual pick isn't overwritten
    // because the effect re-runs only on a store refetch, after which
    // selections have been cleared and an owned level is no longer offered.
    if (offered(store, level).includes(kind)) {
      setLevelSel((prev) => ({ ...prev, [level]: kind }));
    }
  }, [store, rec]);

  const priceOf = useMemo(() => {
    const map = new Map<string, number>();
    if (store) {
      for (const p of store.content_packs) map.set(p.code, p.price_minor);
      for (const p of store.credit_packs) map.set(p.code, p.price_minor);
    }
    return map;
  }, [store]);

  if (!store) return <Spinner />;

  const cartCodes = [
    ...Object.entries(levelSel).map(([lv, kind]) => packCode(lv, kind)),
    ...creditSel,
  ];
  const total = cartCodes.reduce((sum, c) => sum + (priceOf.get(c) ?? 0), 0);

  const pickLevel = (level: string, kind: Kind) => {
    setOrder(null);
    setLevelSel((prev) => {
      const next = { ...prev };
      if (next[level] === kind) delete next[level]; // click again to clear
      else next[level] = kind;
      return next;
    });
  };

  const toggleCredit = (code: string) => {
    setOrder(null);
    setCreditSel((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const placeOrder = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await api.checkout(cartCodes);
      setOrder(result); // show bank-transfer instructions
      setLevelSel({});
      setCreditSel([]);
    } catch {
      setError(t("store.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-28">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-ink">{t("store.title")}</h1>
          <p className="mt-1 text-muted">{t("store.subtitle")}</p>
        </div>
        {balance !== null && (
          <div className="rounded-card border border-line bg-surface px-4 py-2 text-right">
            <p className="text-xs uppercase text-muted">{t("store.creditBalance")}</p>
            <p className="text-2xl font-extrabold text-ink">{balance}</p>
          </div>
        )}
      </div>

      {order && <PaymentInstructions order={order} />}

      <section>
        <h2 className="mb-3 text-xl font-bold text-ink">{t("store.levels")}</h2>
        <div className="flex flex-col gap-3">
          {LEVEL_ORDER.map((level) => {
            const options = offered(store, level);
            const owned = options.length === 0;
            return (
              <div key={level} className="rounded-card border border-line bg-surface p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="w-10 text-lg font-extrabold text-ink">{level}</span>
                  {owned ? (
                    <Badge tone="success">{t("store.owned")}</Badge>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {options.map((kind) => {
                        const selected = levelSel[level] === kind;
                        const isRec = rec === packCode(level, kind);
                        return (
                          <button
                            key={kind}
                            type="button"
                            onClick={() => pickLevel(level, kind)}
                            aria-pressed={selected}
                            aria-label={`${level} ${t(KIND_LABEL[kind])}`}
                            className={`rounded-control border px-3 py-2 text-sm font-semibold transition-colors ${
                              selected
                                ? "border-brand bg-brand text-white"
                                : "border-line bg-canvas text-ink hover:border-brand"
                            }`}
                          >
                            {t(KIND_LABEL[kind])}
                            <span className={selected ? "text-white/80" : "text-muted"}>
                              {" · "}
                              {formatMinor(priceOf.get(packCode(level, kind)) ?? 0, store.currency)}
                            </span>
                            {isRec && (
                              <span className="ml-2 rounded-full bg-bronze px-1.5 py-0.5 text-[10px] font-bold text-white">
                                {t("store.recommended")}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="mb-1 text-xl font-bold text-ink">{t("store.creditsTitle")}</h2>
        <p className="mb-3 text-sm text-muted">{t("store.creditsBody")}</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {store.credit_packs.map((pack) => {
            const selected = creditSel.includes(pack.code);
            return (
              <button
                key={pack.code}
                type="button"
                onClick={() => toggleCredit(pack.code)}
                aria-pressed={selected}
                aria-label={pack.name}
                className={`rounded-card border p-4 text-left transition-colors ${
                  selected ? "border-brand bg-brand-soft" : "border-line bg-surface hover:border-brand"
                }`}
              >
                <h3 className="font-bold text-ink">{pack.name}</h3>
                <p className="mt-1 text-sm text-muted">
                  {t("store.credits", { count: pack.credits })}
                </p>
                <p className="mt-2 font-bold text-ink">
                  {formatMinor(pack.price_minor, store.currency)}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {/* sticky cart summary */}
      <div className="fixed inset-x-0 bottom-0 border-t border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
          <div>
            <p className="text-xs uppercase text-muted">{t("store.total")}</p>
            <p className="text-xl font-extrabold text-ink">
              {formatMinor(total, store.currency)}
              <span className="ml-2 text-sm font-normal text-muted">
                {t("store.itemCount", { count: cartCodes.length })}
              </span>
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          {canOrder ? (
            <Button onClick={placeOrder} disabled={cartCodes.length === 0} loading={busy}>
              {t("store.placeOrder")}
            </Button>
          ) : (
            <p className="max-w-sm text-sm font-semibold text-muted">{t("store.adminOnly")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** After an order is placed: what to pay, where to send it, and the
 *  reference to quote. No online payment — the learner transfers manually
 *  and a super-admin unlocks the packs once it arrives. */
function PaymentInstructions({ order }: { order: OrderInstructions }) {
  const t = useT();
  const p = order.payment;
  const rows: [string, string][] = [
    [t("store.accountName"), p.account_name],
    [t("store.accountNumber"), p.account_number],
    [t("store.bankName"), p.bank_name],
    [t("store.ifsc"), p.ifsc],
  ];
  return (
    <div role="status" className="rounded-card border border-bronze bg-surface p-5 shadow-sm">
      <h2 className="text-lg font-extrabold text-ink">{t("store.orderPlaced")}</h2>
      <p className="mt-1 text-sm text-muted">{p.note}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs uppercase text-muted">{t("store.amountToPay")}</p>
          <p className="text-2xl font-extrabold text-bronze-dark">
            {formatMinor(order.amount_minor, order.currency)}
          </p>
          <p className="mt-3 text-xs uppercase text-muted">{t("store.reference")}</p>
          <p className="select-all font-mono text-lg font-bold text-ink">{order.reference}</p>
          <p className="mt-1 text-xs text-muted">{t("store.quoteReference")}</p>
        </div>

        <div className="rounded-control bg-canvas p-4">
          <p className="text-xs uppercase text-muted">{t("store.transferTo")}</p>
          <dl className="mt-2 space-y-1 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-muted">{label}</dt>
                <dd className="font-semibold text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="mt-4 border-t border-line pt-3">
        <p className="text-xs uppercase text-muted">{t("store.orderItems")}</p>
        <ul className="mt-1 text-sm text-ink">
          {order.items.map((it) => (
            <li key={it.code} className="flex justify-between">
              <span>{it.name}</span>
              <span className="text-muted">{formatMinor(it.price_minor, order.currency)}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-4 text-sm text-muted">
        {t("store.contact")}{" "}
        <a href={`mailto:${p.contact_email}`} className="font-semibold text-brand hover:underline">
          {p.contact_email}
        </a>
      </p>
    </div>
  );
}
